import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga';
import Modal from '../../components/modal/Modal';
import { Button, ActionButtonSpinner, getActionButtonLoadingClassName } from '@ui';
import { getCookie } from '../../hookServices/cookiesManager';
import { resizeImageToJpeg, submitFeedback } from '../../hookServices/feedbackApi';
import styles from './LeaveFeedbackModal.module.css';

const TEXT_MIN = 3;
const TEXT_MAX = 2000;
const RATING_MIN = 1;
const RATING_MAX = 5;
const RATING_DEFAULT = 5;
const KW_MIN = 1;
const KW_MAX = 400;
const KW_DEFAULT = 50;

function readCachedLocation() {
  const cached = getCookie('userLocation');
  if (cached?.latitude == null || cached?.longitude == null) return null;
  const lat = Number(cached.latitude);
  const lon = Number(cached.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return { lat, lon };
}

export default function LeaveFeedbackModal({
  modalOpen,
  setModalOpen,
  station,
  stationNumber,
  clientId,
  onSubmitted,
  canLeaveFeedback = true,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [text, setText] = useState('');
  const [rating, setRating] = useState(RATING_DEFAULT);
  const [carMaxKw, setCarMaxKw] = useState(KW_DEFAULT);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [userLoc, setUserLoc] = useState(null);
  const [locStatus, setLocStatus] = useState('idle');
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const [success, setSuccess] = useState(false);

  const stationLat = station?.latitude != null ? Number(station.latitude) : null;
  const stationLon = station?.longitude != null ? Number(station.longitude) : null;

  const canSubmit =
    text.trim().length >= TEXT_MIN &&
    text.trim().length <= TEXT_MAX &&
    rating >= RATING_MIN &&
    rating <= RATING_MAX &&
    carMaxKw >= KW_MIN &&
    carMaxKw <= KW_MAX &&
    !submitting;

  useEffect(() => {
    if (!modalOpen) return undefined;
    setText('');
    setRating(RATING_DEFAULT);
    setCarMaxKw(KW_DEFAULT);
    setPhotoFile(null);
    setPhotoPreview(null);
    setErrorKey(null);
    setSuccess(false);
    setSubmitting(false);
    setLocStatus('pending');

    const cached = readCachedLocation();
    if (cached) setUserLoc(cached);

    if (!navigator.geolocation) {
      setLocStatus(cached ? 'cached' : 'unavailable');
      return undefined;
    }

    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      pos => {
        if (cancelled) return;
        setUserLoc({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setLocStatus('ok');
      },
      () => {
        if (cancelled) return;
        setLocStatus(cached ? 'cached' : 'denied');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60_000 }
    );
    return () => {
      cancelled = true;
    };
  }, [modalOpen]);

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  function handlePhotoChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setErrorKey(null);
  }

  function clearPhoto() {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function setKwFromInput(raw) {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    setCarMaxKw(Math.min(KW_MAX, Math.max(KW_MIN, Math.round(n))));
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorKey(null);
    try {
      let photoBlob = null;
      if (photoFile) {
        photoBlob = await resizeImageToJpeg(photoFile);
      }
      await submitFeedback({
        stationNumber,
        clientId,
        text: text.trim(),
        rating,
        carMaxKw,
        lat: userLoc?.lat ?? null,
        lon: userLoc?.lon ?? null,
        stationLat,
        stationLon,
        photo: photoBlob,
      });
      ReactGA.event({
        category: 'feedback',
        action: 'submit',
        label: String(stationNumber || ''),
      });
      setSuccess(true);
      onSubmitted?.();
    } catch (err) {
      const detail = err?.detail || err?.message || '';
      if (err?.status === 429 || detail === 'RATE_LIMITED') {
        setErrorKey('rateLimited');
      } else if (err?.status === 403 || detail === 'PAYMENT_REQUIRED') {
        setErrorKey('paymentRequired');
      } else if (err?.status === 413 || detail === 'BODY_TOO_LARGE') {
        setErrorKey('tooLarge');
      } else {
        setErrorKey('generic');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal modalOpen={modalOpen} setModalOpen={setModalOpen} scrollable>
      <div className={styles.root}>
        <button type="button" className={styles.close} aria-label={t('close')} onClick={() => setModalOpen(false)}>
          ×
        </button>

        {success ? (
          <>
            <h2 className={styles.title}>{t('feedback.successTitle')}</h2>
            <p className={styles.successBody}>{t('feedback.successBody')}</p>
            <Button onClick={() => setModalOpen(false)} className={styles.submit} style={{ marginTop: '16px' }}>
              <span>OK</span>
            </Button>
          </>
        ) : !canLeaveFeedback ? (
          <>
            <h2 className={styles.title}>{t('feedback.title')}</h2>
            <p className={styles.successBody}>{t('feedback.errors.paymentRequired')}</p>
            <Button onClick={() => setModalOpen(false)} className={styles.submit} style={{ marginTop: '16px' }}>
              <span>OK</span>
            </Button>
          </>
        ) : (
          <>
            <h2 className={styles.title}>{t('feedback.title')}</h2>

            <label className={styles.label} htmlFor="feedback-text">
              {t('feedback.textLabel')}
            </label>
            <textarea
              id="feedback-text"
              className={styles.textarea}
              value={text}
              onChange={e => setText(e.target.value.slice(0, TEXT_MAX))}
              rows={4}
              placeholder={t('feedback.textPlaceholder')}
            />
            <div className={styles.counter}>
              {text.trim().length}/{TEXT_MAX}
            </div>

            <div className={styles.label}>{t('feedback.ratingLabel')}</div>
            <div className={styles.stars} role="radiogroup" aria-label={t('feedback.ratingLabel')}>
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={rating === n}
                  aria-label={`${n}`}
                  className={`${styles.star} ${n <= rating ? styles.starActive : ''}`}
                  onClick={() => setRating(n)}
                  onKeyDown={e => {
                    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                      e.preventDefault();
                      setRating(Math.min(RATING_MAX, rating + 1));
                    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                      e.preventDefault();
                      setRating(Math.max(RATING_MIN, rating - 1));
                    }
                  }}
                >
                  ★
                </button>
              ))}
            </div>

            <div className={styles.kwRow}>
              <label className={styles.label} htmlFor="feedback-kw-range" style={{ margin: 0 }}>
                {t('feedback.maxKwLabel')}
              </label>
              <span className={styles.kwValue}>
                {carMaxKw} {t('feedback.kwUnit')}
              </span>
            </div>
            <input
              id="feedback-kw-range"
              className={styles.range}
              type="range"
              min={KW_MIN}
              max={KW_MAX}
              step={1}
              value={carMaxKw}
              onChange={e => setKwFromInput(e.target.value)}
              aria-valuemin={KW_MIN}
              aria-valuemax={KW_MAX}
              aria-valuenow={carMaxKw}
            />

            <label className={styles.label} htmlFor="feedback-photo">
              {t('feedback.carPhotoLabel')} ({t('feedback.optional')})
            </label>
            <input
              id="feedback-photo"
              ref={fileInputRef}
              className={styles.fileInput}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
            />
            <label className={styles.fileBtn} htmlFor="feedback-photo">
              {photoFile ? photoFile.name : t('feedback.choosePhoto')}
            </label>
            {photoPreview && (
              <div className={styles.preview}>
                <img className={styles.previewImg} src={photoPreview} alt="" />
                <Button type="button" variant="outline-alt" onClick={clearPhoto}>
                  {t('feedback.removePhoto')}
                </Button>
              </div>
            )}

            {locStatus === 'pending' && <p className={styles.locating}>{t('feedback.locating')}</p>}

            {errorKey && <p className={styles.error}>{t(`feedback.errors.${errorKey}`)}</p>}

            <Button
              type="button"
              className={`${getActionButtonLoadingClassName(submitting)} ${styles.submit}`}
              disabled={!canSubmit}
              aria-busy={submitting}
              onClick={handleSubmit}
            >
              {submitting ? <ActionButtonSpinner /> : <span>{t('feedback.submit')}</span>}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
